import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import "../MicrositeRenderer.css";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";

const GOOGLE_MAPS_API_KEY = "AIzaSyCN0ylKmtKK6Vz9e90PZOOb1kNpDWdigF4";
const FALLBACK_IMAGE =
  "https://via.placeholder.com/1200x400?text=Event+Coming+Soon";

const DEFAULT_RSVP_SETTINGS = {
  collectEmail: true,
  collectPhone: false,
  collectPartySize: true,
  collectAdultsKids: true,
  collectKidsAges: false,
  collectGender: false,
  collectNotes: true,
  customQuestions: [],
};

const MicrositeRenderer = ({ event, preview = false, organizationId }) => {
  if (!event) return null;

  const [description, setDescription] = useState("");
  const [bannerUrl, setBannerUrl] = useState(null);
  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [submittingRsvp, setSubmittingRsvp] = useState(false);
  const [rsvpError, setRsvpError] = useState("");

  const [rsvpForm, setRsvpForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    partySize: "",
    adultsCount: "",
    kidsCount: "",
    kidsAges: "",
    gender: "",
    notes: "",
  });

  const config = event?.micrositeConfig || {};

  const settings = useMemo(() => {
    return {
      ...DEFAULT_RSVP_SETTINGS,
      ...(event?.rsvpSettings || {}),
    };
  }, [event]);

  useEffect(() => {
    if (event.description) {
      setDescription(event.description);
    } else if (config.eventDescription) {
      setDescription(config.eventDescription);
    } else {
      setDescription("");
    }

    if (config.backgroundImageUrl) {
      setBackgroundUrl(config.backgroundImageUrl);
      setBannerUrl(null);
    } else if (event.eventImage?.trim()) {
      setBannerUrl(event.eventImage);
      setBackgroundUrl(null);
    } else {
      setBannerUrl(FALLBACK_IMAGE);
      setBackgroundUrl(null);
    }
  }, [event, config.eventDescription, config.backgroundImageUrl]);

  // Close modal on ESC
  useEffect(() => {
    if (!showRsvpModal) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowRsvpModal(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showRsvpModal]);

  const formattedDate = event?.startDate
    ? new Date(event.startDate).toLocaleDateString([], {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const formattedTime = event?.startTime
    ? new Date(event.startTime).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : null;

  const mapSrc =
    event?.locationLat && event?.locationLng
      ? `https://www.google.com/maps/embed/v1/view?key=${GOOGLE_MAPS_API_KEY}&center=${event.locationLat},${event.locationLng}&zoom=14`
      : null;

  const primaryActionType = config.primaryActionType || "";
  const allowRsvps = event?.allowRsvps !== false;

  const handleRsvpField = (field) => (e) => {
    const value = e?.target?.value ?? "";
    setRsvpForm((prev) => ({ ...prev, [field]: value }));
  };

  const buildAttendeePayload = () => {
    const attendee = {
      fullName: rsvpForm.fullName || "",
    };

    if (settings.collectEmail) attendee.email = rsvpForm.email || "";
    if (settings.collectPhone) attendee.phone = rsvpForm.phone || "";

    if (settings.collectPartySize) attendee.partySize = rsvpForm.partySize || null;

    if (settings.collectAdultsKids) {
      attendee.adultsCount = rsvpForm.adultsCount || null;
      attendee.kidsCount = rsvpForm.kidsCount || null;
    }

    if (settings.collectKidsAges) {
      attendee.kidsAges = rsvpForm.kidsAges
        ? rsvpForm.kidsAges
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean)
        : [];
    }

    if (settings.collectGender) attendee.gender = rsvpForm.gender || "";
    if (settings.collectNotes) attendee.notes = rsvpForm.notes || "";

    return attendee;
  };

  const resetRsvpForm = useCallback(() => {
    setRsvpForm({
      fullName: "",
      email: "",
      phone: "",
      partySize: "",
      adultsCount: "",
      kidsCount: "",
      kidsAges: "",
      gender: "",
      notes: "",
    });
  }, []);

  const handleSubmitRsvp = async () => {
  setSubmittingRsvp(true);
  setRsvpError("");

  try {
    const orgHeader = organizationId || event.organizationId;
    if (!orgHeader) {
      setRsvpError("Missing organization ID.");
      return;
    }

    const res = await axios.post(
      "https://submitrsvp-774073159618.us-central1.run.app",
      {
        eventId: event.eventId,
        attendee: buildAttendeePayload(),
      },
      {
        headers: { "organization-id": orgHeader },
      }
    );

    // If the CF ever returns a 200 with { status: "blocked", message: ... }
    if (res.data?.status === "blocked") {
      setRsvpError(res.data.message || "We couldn’t confirm your RSVP.");
      return;
    }

    // Success path
    resetRsvpForm();
    setShowRsvpModal(false);
  } catch (err) {
    const resp = err?.response;
    const data = resp?.data;

    // 403: audience match failed → show noMatchMessage from CF
    if (resp?.status === 403 && data?.code === "NO_AUDIENCE_MATCH") {
      setRsvpError(
        data?.message ||
          "We couldn’t confirm your RSVP. Please contact the event organizer."
      );
    }
    // 400: validation errors (missing fields)
    else if (Array.isArray(data?.missingFields) && data.missingFields.length) {
      setRsvpError(`Please fill: ${data.missingFields.join(", ")}`);
    }
    // Fallback
    else {
      setRsvpError("Failed to submit RSVP.");
    }
  } finally {
    setSubmittingRsvp(false);
  }
};

function linkify(text) {
  if (!text) return text;

  // match http://, https://, or www.
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

  return text.split(urlRegex).map((part, i) => {
    if (part.match(urlRegex)) {
      const url = part.startsWith("http") ? part : `https://${part}`;
      return (
        <a
          key={i}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#007bff", textDecoration: "underline" }}
        >
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

  const renderPrimaryAction = () => {
    if (!config.callToAction) return null;

    if (primaryActionType === "rsvp") {
      if (!allowRsvps) return null;

      return (
        <button
          className="cta-button"
          onClick={() => setShowRsvpModal(true)}
        >
          {config.callToAction}
        </button>
      );
    }

    if (primaryActionType === "donation") {
      return (
        <button className="cta-button" onClick={() => {}}>
          {config.callToAction}
        </button>
      );
    }

    if (primaryActionType === "ticket") {
      return (
        <button className="cta-button" onClick={() => {}}>
          {config.callToAction}
        </button>
      );
    }

    return (
      <button className="cta-button" onClick={() => {}}>
        {config.callToAction}
      </button>
    );
  };

  return (
    <div
      className="microsite-container"
      style={{
        backgroundColor: config.backgroundColor || "#ffffff",
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        color: config.textColor || "#000000",
        transition: "all 0.3s ease",
        minHeight: "100vh",
      }}
    >
      {bannerUrl && !backgroundUrl && (
        <div className="microsite-banner-container">
          <img
            src={bannerUrl}
            alt="Event Banner"
            className="banner"
            onError={(e) => {
              e.target.src = FALLBACK_IMAGE;
            }}
          />
        </div>
      )}

      <div className="microsite-content">
        <div
          style={{
            backgroundColor: config.textShadingColor
              ? `rgba(${config.textShadingColor.r},${config.textShadingColor.g},${config.textShadingColor.b},${config.textShadingColor.a})`
              : "transparent",
            padding: "1.5rem",
            borderRadius: "8px",
            textAlign: "center",
            marginBottom: "1rem",
            transition: "background-color 0.5s ease",
          }}
        >
          <h1 className="event-title" style={{ color: config.textColor }}>
            {event.eventName}
          </h1>
          <h3 className="event-type" style={{ color: config.textColor }}>
            {event.eventType}
          </h3>

          {formattedDate && (
            <div className="event-detail" style={{ marginTop: "0.5rem" }}>
              <span className="event-icon">📅</span>
              <span>
                {formattedDate}
                {formattedTime && ` at ${formattedTime}`}
              </span>
            </div>
          )}

          {event.location && (
            <div className="event-detail">
              <span className="event-icon">📍</span>
              <span>{event.location}</span>
            </div>
          )}
        </div>

        {mapSrc && (
          <div className="event-map">
            <iframe
              title="Event Map"
              width="100%"
              height="250"
              style={{ border: 0, marginTop: "1rem" }}
              loading="lazy"
              allowFullScreen
              src={mapSrc}
            ></iframe>
          </div>
        )}

        <hr />

        <h3 style={{ color: config.textColor }}>
          {config.donationHeadline || ""}
        </h3>

        <div className="cta-row">
          {renderPrimaryAction()}

          {config.includeDonateButton && (
            <button className="cta-button secondary">Donate Now</button>
          )}
        </div>

        <hr />

        {config.secondaryCTA && (
          <p className="secondary-cta">
            {config.secondaryCTA}
          </p>
        )}

        {description && (
          <div className="event-description">
            <h4 className="section-title">About the Event</h4>
            <p>{description}</p>
          </div>
        )}

        {event.contact?.name && (
          <div className="contact-info">
            <h4 className="section-title">Contact Us</h4>
            <div className="contact-card">
              <div className="contact-row">
                <span className="contact-label">Name</span>
                <span className="contact-value">{event.contact.name}</span>
              </div>
              {event.contact.email && (
                <div className="contact-row">
                  <span className="contact-label">Email</span>
                  <span className="contact-value">{event.contact.email}</span>
                </div>
              )}
              {event.contact.phone && (
                <div className="contact-row">
                  <span className="contact-label">Phone</span>
                  <span className="contact-value">{event.contact.phone}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {event.registrationDeadline && (
          <div className="registration-deadline">
            <h5 className="section-title">Register By</h5>
            <p>{new Date(event.registrationDeadline).toLocaleDateString()}</p>
          </div>
        )}

        {Array.isArray(event.eventTags) && event.eventTags.length > 0 && (
          <div className="tags">
            <h5 className="section-title">Tags</h5>
            <ul>
              {event.eventTags.map((tag, idx) => (
                <li key={idx}>{tag}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="share-bar-inline">
          <h4 className="section-title">Share This Event</h4>
          <div className="share-icons">
            <a
              href={`sms:?&body=Check out this event: ${window.location.href}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <SmsIcon />
            </a>
            <a
              href={`mailto:?subject=Check out this event&body=${window.location.href}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <EmailIcon />
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=Check out this event&url=${window.location.href}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <TwitterIcon />
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FacebookIcon />
            </a>
          </div>
        </div>
      </div>

      {showRsvpModal && (
        <div
          className="microsite-modal-overlay"
          onClick={() => setShowRsvpModal(false)}
        >
          <div
            className="microsite-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="RSVP Modal"
          >
            <button
              className="microsite-modal-close"
              onClick={() => setShowRsvpModal(false)}
              aria-label="Close RSVP modal"
            >
              ×
            </button>

            <div className="microsite-modal-header">
              <h2>{event.eventName || "RSVP"}</h2>
              <p className="microsite-modal-subtitle">
                RSVP to let us know you’re coming.
              </p>
            </div>

            <div className="microsite-modal-divider" />

            <div className="rsvp-form">
              <input
                className="rsvp-input"
                placeholder="Full Name"
                value={rsvpForm.fullName}
                onChange={handleRsvpField("fullName")}
              />

              {settings.collectEmail && (
                <input
                  className="rsvp-input"
                  placeholder="Email"
                  value={rsvpForm.email}
                  onChange={handleRsvpField("email")}
                />
              )}

              {settings.collectPhone && (
                <input
                  className="rsvp-input"
                  placeholder="Phone Number"
                  value={rsvpForm.phone}
                  onChange={handleRsvpField("phone")}
                />
              )}

              {settings.collectPartySize && (
                <input
                  className="rsvp-input"
                  placeholder="Party Size"
                  type="number"
                  value={rsvpForm.partySize}
                  onChange={handleRsvpField("partySize")}
                />
              )}

              {settings.collectAdultsKids && (
                <div className="rsvp-row">
                  <input
                    className="rsvp-input"
                    placeholder="Adults"
                    type="number"
                    value={rsvpForm.adultsCount}
                    onChange={handleRsvpField("adultsCount")}
                  />
                  <input
                    className="rsvp-input"
                    placeholder="Kids"
                    type="number"
                    value={rsvpForm.kidsCount}
                    onChange={handleRsvpField("kidsCount")}
                  />
                </div>
              )}

              {settings.collectKidsAges && (
                <input
                  className="rsvp-input"
                  placeholder="Kids ages (comma-separated)"
                  value={rsvpForm.kidsAges}
                  onChange={handleRsvpField("kidsAges")}
                />
              )}

              {settings.collectGender && (
                <select
                  className="rsvp-input"
                  value={rsvpForm.gender}
                  onChange={handleRsvpField("gender")}
                >
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              )}

              {settings.collectNotes && (
                <textarea
                  className="rsvp-input"
                  placeholder="Notes / Special Requests"
                  value={rsvpForm.notes}
                  onChange={handleRsvpField("notes")}
                  rows={3}
                />
              )}

              {rsvpError && (
                <p className="rsvp-error">
                  {linkify(rsvpError)}
                </p>
              )}

              <div className="rsvp-actions">
                <button
                  className="cta-button"
                  onClick={handleSubmitRsvp}
                  disabled={submittingRsvp}
                >
                  {submittingRsvp ? "Submitting..." : "Submit RSVP"}
                </button>

                <button
                  className="cta-button secondary"
                  onClick={() => {
                    resetRsvpForm();
                    setShowRsvpModal(false);
                  }}
                  disabled={submittingRsvp}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MicrositeRenderer;